// deps
import { useCallback, useContext, useEffect, useMemo, useRef } from "react";

// types
import { CroutonIds, CroutonPriority, ICroutonList, ShowCroutonArgs } from "@swiggy-private/cart-types";

// context
import { CartContext } from "@cart-client/contexts/CartContext";
import { CartOverlaysContext } from "@swiggy-private/checkout-core";
import { CartIPLSwiggySixesContext } from "@swiggy-private/core-ui";
import { CartFlowContext } from "@cart-client/contexts/CartFlowContext";
// utils
import { isEmpty } from "@swiggy-private/js-utils";
import { isMROFlow } from "@cart-client/helpers/MROFoodCart";

// hooks
import { useIPLSwiggySixesCartCroutonAndUpdates } from "@cart-client/hooks/useIPLSwiggySixesCartCroutonAndUpdates";
import { useIPLSwiggySixesTimerCrouton } from "@cart-client/hooks/useIPLSwiggySixesTimerCrouton";

export interface UseIPLSwiggySixesCroutonRegistrationProps {
    setCroutonVisibility: (croutonVisibility: boolean) => void;
}

export interface UseIPLSwiggySixesCroutonRegistrationReturnType {
    getIPLSwiggySixesCroutonRegistration: (crouton: ICroutonList, index: number) => ShowCroutonArgs | null;
}

/**
 * Owns the IPL Swiggy Sixes crouton registration for the MRO flow: the cart/JS-bridge timer
 * updates, the crouton payload, the priority decision and the per-tick content refresh.
 *
 * Exposes a registration callback so the crouton-list driven registration in useGenericCrouton
 * can treat IPL like any other supported crouton, while the tick refresh and the JS-bridge-only
 * six-hit (no list entry) are registered from here.
 */
const useIPLSwiggySixesCroutonRegistration = ({
    setCroutonVisibility,
}: UseIPLSwiggySixesCroutonRegistrationProps): UseIPLSwiggySixesCroutonRegistrationReturnType => {
    const { cartState } = useContext(CartContext);
    const { registerCrouton } = useContext(CartOverlaysContext);
    // Live IPL offer expiry from the shared timer store (cart response + JS-bridge six-hits).
    const { timestampInMs } = useContext(CartIPLSwiggySixesContext);
    const { reviewedCart: cartResponse } = cartState;
    const croutonList = useMemo(() => cartResponse?.data?.croutonList, [cartResponse]);
    const { data } = useContext(CartFlowContext);
    const isMroFlow = isMROFlow(data?.cartSource);

    // In non-MRO the renderer's direct call owns the IPL timer + timestamp update, so skip it here
    // to avoid duplicating the side-effect. In MRO this hook is the sole owner.
    const { croutonData, isFromJSBridge } = useIPLSwiggySixesCartCroutonAndUpdates({
        cartResponse,
        // Non-MRO: CartRenderer owns timer updates. MRO: this registration path is the sole owner.
        skipTimerUpdate: !isMroFlow,
    });

    // skipSelfRegister keeps this instance from self-registering (registration is driven from this
    // hook in MRO) and keeps it fully inert in non-MRO, leaving the renderer's direct call as the
    // sole non-MRO driver.
    const { payloadForCroutonRegister: iplSwiggySixesPayload } = useIPLSwiggySixesTimerCrouton({
        croutonData,
        setCroutonVisibility,
        skipSelfRegister: true,
    });

    // Used as the IPL crouton's endTimestamp so timer updates propagate to the MRO tiebreak.
    const endTimestampInSec = (timestampInMs || 0) / 1000;

    const getIPLSwiggySixesCroutonRegistration = useCallback(
        (crouton: ICroutonList, index: number): ShowCroutonArgs | null => {
            // Content is driven by the payload exposed from useIPLSwiggySixesTimerCrouton and IPL is
            // registered through the crouton list only in MRO.
            if (!isMroFlow || isEmpty(iplSwiggySixesPayload?.title)) {
                return null;
            }

            return {
                id: crouton.type,
                // On a live six-hit pushed via the app JS bridge we register with a static
                // PRIORITY_ORDER_ONE (independent of list order) so it can take the slot over the
                // RX closing timer; otherwise priority follows the crouton list index.
                priority: isFromJSBridge ? CroutonPriority.PRIORITY_ORDER_ONE : index + 1,
                content: {
                    ...iplSwiggySixesPayload,
                    setCroutonVisibility,
                    endTimestamp: endTimestampInSec,
                },
                forceRefreshContent: true,
            };
        },
        [isMroFlow, iplSwiggySixesPayload, isFromJSBridge, setCroutonVisibility, endTimestampInSec],
    );

    const iplIndexInList = croutonList?.findIndex((crouton) => crouton.type === CroutonIds.IPL_SWIGGY_SIXES) ?? -1;

    // Register from here when IPL is part of the crouton list, or when a JS bridge six-hit wants it
    // regardless of the list (synthetic entry, static priority handled in the callback above).
    const shouldRegisterIpl = isMroFlow && (iplIndexInList >= 0 || isFromJSBridge);

    const prevCroutonListRef = useRef<typeof croutonList>();
    // True only on the render where `croutonList` identity changes (cart update). Ref is updated
    // immediately after, so later ticks with the same list see false and can IPL-refresh here.
    const isFreshCroutonList = prevCroutonListRef.current !== croutonList;
    prevCroutonListRef.current = croutonList;

    // Read off a ref so a crouton list update on its own never re-runs the effect below: that pass
    // is owned by the list loop in useGenericCrouton.
    const croutonListStateRef = useRef({ croutonList, iplIndexInList, isFreshCroutonList });
    croutonListStateRef.current = { croutonList, iplIndexInList, isFreshCroutonList };

    // The IPL content keeps updating every tick (precalculated timer + cycling comms), so this
    // effect refreshes that single crouton whenever the registration payload changes - re-running
    // the full list loop each tick would re-fire impressions for the other displayed croutons.
    useEffect(() => {
        if (!shouldRegisterIpl) {
            return;
        }

        const {
            croutonList: list,
            iplIndexInList: index,
            isFreshCroutonList: isFreshList,
        } = croutonListStateRef.current;

        // Fresh cart list: useGenericCrouton already registers IPL in the same commit — skip to
        // avoid a double register. Same-list ticks (isFreshList=false) fall through and refresh.
        if (index >= 0 && isFreshList) {
            return;
        }

        const iplCrouton: ICroutonList =
            index >= 0 && list ? list[index] : { type: CroutonIds.IPL_SWIGGY_SIXES, data: {} };
        const registrationArgs = getIPLSwiggySixesCroutonRegistration(iplCrouton, index);

        if (!isEmpty(registrationArgs)) {
            registerCrouton(registrationArgs as ShowCroutonArgs);
        }
    }, [shouldRegisterIpl, getIPLSwiggySixesCroutonRegistration, registerCrouton]);

    return { getIPLSwiggySixesCroutonRegistration };
};

export default useIPLSwiggySixesCroutonRegistration;
